import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';

interface CompliancePanelProps {
  report: any;
  onClose: () => void;
}

interface ComplianceIssue {
  issue_type: string;
  severity: string;
  field: string;
  message: string;
  regulation?: string;
  resolution?: string;
}

const CompliancePanel: React.FC<CompliancePanelProps> = ({ report, onClose }) => {
  const theme = useTheme();
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
        return <InfoIcon color="info" />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity: string): any => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const getComplianceScore = () => {
    if (!report || !report.issues) return 100;
    
    const totalIssues = report.issues.length;
    const criticalIssues = report.critical_count || 0;
    const warningIssues = report.warning_count || 0;
    
    if (criticalIssues > 0) return 25;
    if (warningIssues > 2) return 50;
    if (warningIssues > 0) return 75;
    if (totalIssues > 0) return 90;
    return 100;
  };

  const groupIssuesByType = () => {
    if (!report || !report.issues) return {};
    
    return report.issues.reduce((acc: any, issue: ComplianceIssue) => {
      if (!acc[issue.issue_type]) {
        acc[issue.issue_type] = [];
      }
      acc[issue.issue_type].push(issue);
      return acc;
    }, {});
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'customs':
        return <GavelIcon />;
      case 'export_control':
      case 'sanctions':
        return <SecurityIcon />;
      case 'dangerous_goods':
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const formatTypeName = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const complianceScore = getComplianceScore();
  const groupedIssues = groupIssuesByType();

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      sx={{
        width: 450,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 450,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Compliance Report</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Compliance Score */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Overall Compliance Score
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={complianceScore}
                color={complianceScore >= 75 ? 'success' : complianceScore >= 50 ? 'warning' : 'error'}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
            <Typography variant="h6" sx={{ color: complianceScore >= 75 ? theme.palette.success.main : complianceScore >= 50 ? theme.palette.warning.main : theme.palette.error.main }}>
              {complianceScore}%
            </Typography>
          </Box>
        </Box>

        {/* Summary Alert */}
        {report && (
          <Alert
            severity={report.compliant ? 'success' : complianceScore < 50 ? 'error' : 'warning'}
            sx={{ mb: 2 }}
          >
            {report.compliant ? (
              'Document is fully compliant with all regulations'
            ) : (
              <>
                <Typography variant="subtitle2">Compliance Issues Detected</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  {report.critical_count > 0 && (
                    <Chip
                      label={`${report.critical_count} Critical`}
                      size="small"
                      color="error"
                    />
                  )}
                  {report.warning_count > 0 && (
                    <Chip
                      label={`${report.warning_count} Warnings`}
                      size="small"
                      color="warning"
                    />
                  )}
                  {report.info_count > 0 && (
                    <Chip
                      label={`${report.info_count} Info`}
                      size="small"
                    />
                  )}
                </Box>
              </>
            )}
          </Alert>
        )}

        {/* Checked Regulations */}
        {report?.checked_regulations && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Checked Regulations
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {report.checked_regulations.map((reg: string) => (
                <Chip key={reg} label={reg} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Issues by Type */}
        <Typography variant="subtitle2" gutterBottom>
          Issues by Category
        </Typography>
        
        {Object.keys(groupedIssues).length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckIcon sx={{ fontSize: 48, color: theme.palette.success.main }} />
            <Typography variant="body2" color="text.secondary">
              No compliance issues found
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedIssues).map(([type, issues]: [string, any]) => (
            <Accordion key={type} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {getTypeIcon(type)}
                  <Typography sx={{ flexGrow: 1 }}>
                    {formatTypeName(type)}
                  </Typography>
                  <Chip label={issues.length} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {issues.map((issue: ComplianceIssue, index: number) => (
                    <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {getSeverityIcon(issue.severity)}
                        </ListItemIcon>
                        <ListItemText
                          primary={issue.message}
                          secondary={
                            <>
                              {issue.field && (
                                <Typography variant="caption" component="div">
                                  Field: {issue.field}
                                </Typography>
                              )}
                              {issue.regulation && (
                                <Typography variant="caption" component="div">
                                  Regulation: {issue.regulation}
                                </Typography>
                              )}
                            </>
                          }
                        />
                        <Chip
                          label={issue.severity}
                          size="small"
                          color={getSeverityColor(issue.severity)}
                        />
                      </Box>
                      {issue.resolution && (
                        <Alert severity="info" sx={{ mt: 1, width: '100%' }}>
                          <Typography variant="caption">
                            <strong>Resolution:</strong> {issue.resolution}
                          </Typography>
                        </Alert>
                      )}
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))
        )}

        {/* Timestamp */}
        {report?.timestamp && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            Report generated: {new Date(report.timestamp).toLocaleString()}
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default CompliancePanel;